import {Component, effect, inject, OnInit, Signal, signal} from '@angular/core';
import {Store} from '@ngrx/store';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {SettingsReferenceEnum} from '../../models';
import {selectSelectedSetting} from '../../store/setting.selectors';
import {settingsSetSelected} from '../../store/setting.actions';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {ICrudOptions, IUserAccessFeatureLocalStorageSettings} from '../../../user-access/models/user-access-features/access';
import {
  selectUserAccessFeatureLocalStorageForSelectedStore,
} from '../../../user-access/user-access-store/user-access.selectors';
import {LocalStorageService} from '../../../core/service/local-storage.service';
import {LocalStorageKeysEnum} from '../../../core/models/local-storage-keys';

/**
 * Interface for local storage item display
 */
interface ILocalStorageItem {
  category: string;
  displayName: string;
  key: string;
  value: string | null;
}

/**
 * Interface for local storage category
 */
interface ILocalStorageCategory {
  displayName: string;
  items: ILocalStorageItem[];
  name: string;
}

@Component({
  selector: 'app-feature-local-storage-manager',
  templateUrl: './feature-local-storage-manager.component.html',
  styleUrl: './feature-local-storage-manager.component.scss',
  standalone: false,
})
export class FeatureLocalStorageManagerComponent implements OnInit {

  //#region INJECTORS
  private _store = inject(Store);
  private modalUtils = inject(SharedModalUtils);
  private localStorageService = inject(LocalStorageService);
  //#endregion

  //#region PROPERTIES
  protected storageItems = signal<ILocalStorageItem[]>([]);
  protected categories = signal<ILocalStorageCategory[]>([]);

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;

  private readonly _settingChanged: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedSetting);
  private readonly _uaLocalStorage: Signal<IUserAccessFeatureLocalStorageSettings> = this._store.selectSignal(selectUserAccessFeatureLocalStorageForSelectedStore);

  //#endregion

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });
  }

  //#endregion

  //#region ACCESSOR - USER ACCESS
  get localStorageUserAccess(): IUserAccessFeatureLocalStorageSettings {
    return this._uaLocalStorage();
  }

  get canStorageBeViewed(): boolean {
    const access = this.localStorageUserAccess?.storageManager;
    return access?.canView ?? false;
  }

  get canStorageBeEdited(): boolean {
    const access = this.localStorageUserAccess?.storageManager;
    return access?.canEdit ?? false;
  }

  get canStorageBeCleared(): boolean {
    const access = this.localStorageUserAccess?.storageManager;
    return access?.canSubmit ?? false;
  }

  //#endregion

  //#region LIFECYCLE HOOKS
  ngOnInit(): void {
    this.loadStorageItems();
  }

  //#endregion

  //#region STORAGE - LOAD ITEMS
  private loadStorageItems(): void {
    const items: ILocalStorageItem[] = [];
    const categoryMap = new Map<string, ILocalStorageItem[]>();

    Object.values(LocalStorageKeysEnum).forEach((key: string) => {
      const value = localStorage.getItem(key);
      const category = this.getCategoryFromKey(key);
      const displayName = this.getDisplayNameFromKey(key);

      const item: ILocalStorageItem = {
        key,
        value,
        category,
        displayName,
      };

      items.push(item);

      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }
      categoryMap.get(category)?.push(item);
    });

    this.storageItems.set(items);

    const categoriesArray: ILocalStorageCategory[] = [];
    categoryMap.forEach((categoryItems: ILocalStorageItem[], categoryName: string) => {
      categoriesArray.push({
        name: categoryName,
        displayName: this.getCategoryDisplayName(categoryName),
        items: categoryItems,
      });
    });

    this.categories.set(categoriesArray.sort((a: ILocalStorageCategory, b: ILocalStorageCategory) => a.displayName.localeCompare(b.displayName)));
  }

  //#endregion

  //#region STORAGE - HELPERS
  private getCategoryFromKey(key: string): string {
    if (key.startsWith('auth')) return 'auth';
    if (key.startsWith('agGridColDef')) return 'agGrid';
    if (key.startsWith('barcodesScanned')) return 'barcodes';
    if (key.startsWith('ao')) return 'autoOrder';
    return 'other';
  }

  private getCategoryDisplayName(category: string): string {
    const displayNames: Record<string, string> = {
      auth: 'Authentication',
      agGrid: 'Table Settings',
      barcodes: 'Barcode Scanner',
      autoOrder: 'Auto Ordering',
      other: 'Other',
    };
    return displayNames[category] ?? category;
  }

  private getDisplayNameFromKey(key: string): string {
    const displayNames: Record<string, string> = {
      [LocalStorageKeysEnum.authPersistence]: 'Authentication Persistence',
      [LocalStorageKeysEnum.authLastLogin]: 'Last Login',
      [LocalStorageKeysEnum.agGridColDefOrderNgpReport]: 'NGP Report Column Order',
      [LocalStorageKeysEnum.agGridColDefOrderStockManager]: 'Stock Manager Column Order',
      [LocalStorageKeysEnum.agGridColDefSizeNgpReport]: 'NGP Report Column Sizes',
      [LocalStorageKeysEnum.agGridColDefSizeStockManager]: 'Stock Manager Column Sizes',
      [LocalStorageKeysEnum.agGridColDefOrderAddSupplier]: 'Add Supplier Column Order',
      [LocalStorageKeysEnum.agGridColDefSizeAddSupplier]: 'Add Supplier Column Sizes',
      [LocalStorageKeysEnum.agGridColDefOrderAutoOrder]: 'Auto Order Column Order',
      [LocalStorageKeysEnum.agGridColDefOrderQuotes]: 'Quotes Column Order',
      [LocalStorageKeysEnum.agGridColDefSizeQuotes]: 'Quotes Column Sizes',
      [LocalStorageKeysEnum.agGridColDefSizeAutoOrder]: 'Auto Order Column Sizes',
      [LocalStorageKeysEnum.aoOpenOrder]: 'Open Order Data',
      [LocalStorageKeysEnum.barcodesScannedBinLocationUpdate]: 'Bin Location Update Scans',
      [LocalStorageKeysEnum.barcodesScannedOrderCreate]: 'Order Create Scans',
      [LocalStorageKeysEnum.barcodesScannedProcessGRV]: 'Process GRV Scans',
      [LocalStorageKeysEnum.barcodesScannedShelfTalkersPrint]: 'Shelf Talkers Print Scans',
      [LocalStorageKeysEnum.barcodesScannedStockUpdate]: 'Stock Update Scans',
    };
    return displayNames[key] ?? key;
  }

  //#endregion

  //#region STORAGE - CLEAR ACTIONS
  async onClearItem(item: ILocalStorageItem): Promise<void> {
    if (!this.canStorageBeCleared) return;

    const confirmed = await this.showClearConfirmation(
      'Clear Item',
      `Are you sure you want to clear "${item.displayName}"?`,
    );

    if (confirmed) {
      localStorage.removeItem(item.key);
      this.loadStorageItems();
    }
  }

  async onClearCategory(category: ILocalStorageCategory): Promise<void> {
    if (!this.canStorageBeCleared) return;

    const confirmed = await this.showClearConfirmation(
      'Clear Category',
      `Are you sure you want to clear all ${category.items.length} items in "${category.displayName}"?`,
    );

    if (confirmed) {
      category.items.forEach((item: ILocalStorageItem) => {
        localStorage.removeItem(item.key);
      });
      this.loadStorageItems();
    }
  }

  async onClearAll(): Promise<void> {
    if (!this.canStorageBeCleared) return;

    const confirmed = await this.showClearConfirmation(
      'Clear All Storage',
      'Are you sure you want to clear ALL local storage items? This will reset all your saved preferences and settings.',
    );

    if (confirmed) {
      this.localStorageService.clearLocalStorageKeys();
      this.loadStorageItems();
    }
  }

  //#endregion

  //#region MODAL - CONFIRM CLEAR
  private async showClearConfirmation(title: string, message: string): Promise<boolean> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Clear',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: title,
      modalTitleIcon: IconsEnum.alertBoxOutline,
      modalTitleIconColor: IconColourEnum.red,
      contextHeading: message,
      contextDescription: ['This action cannot be undone.'],
      cssClass: ['shared-basic-modal-css-small'],
    };

    const result: IModalDismissResult = await this.modalUtils.createModal(modalProps);
    return result.role === 'modal-accept';
  }

  //#endregion

  //#region HELPERS
  hasValue(item: ILocalStorageItem): boolean {
    return item.value !== null && item.value !== '';
  }

  getItemCount(): number {
    return this.storageItems().filter((item: ILocalStorageItem) => this.hasValue(item)).length;
  }

  getTotalCount(): number {
    return this.storageItems().length;
  }

  //#endregion

}
